/**
 * 🔗 10-01 event links (HOUSE_OCTOBER_UIUX_BATCH_SPEC_2026_10_01 §3.5, Nina's
 * R14: "event cards, by Friday"). ONE share path for the house: the calendar
 * rows' Invite, the outing plate's Share and the event scene's share all come
 * through here.
 *
 * Link previews never run the site's code, so every house link used to unfurl
 * as the crab card. Every DATED house night (pinned + sky + book club) now has
 * a tiny static page, https://myspaice.ai/e/<slug>.html, carrying that night's
 * own picture (generated by reddit-devvit/scripts/gen-share-pages.js; the
 * manifest is src/data/sharePages.generated.js). The page is a poster before
 * the night, takes people straight into the room while it runs, and into the
 * house after.
 *
 *  · Lookup: night (`day|kind`, house + sky only) > template > room (the last
 *    two are wave 2 and empty today). A miss = today's room link,
 *    `${origin}/game-step?gameId=…&room=…&view=peek`, byte for byte.
 *  · Pages always say https://myspaice.ai, whatever domain the sharer is on
 *    (all 4 domains serve the same bucket; an unfurl caches per URL).
 *  · Resident plans (`by`) and engine surprises keep the room link.
 *  · Switch: SHOW_EVENT_SHARE_PAGES ships DARK (false = today's room links
 *    only, byte for byte). Flip it to true in the release commit, and only
 *    after justsomeguy-backend/tools/upload-og-cards.js --apply has
 *    HEAD-verified every picture: an unfurler caches a first preview with no
 *    picture for days.
 */
import { SHARE_PAGES } from '../data/sharePages.generated';

// 🔗 10-01 event links: DARK until the pictures are hosted (false = every share is today's room link)
export const SHOW_EVENT_SHARE_PAGES = false;
export const CANONICAL_ORIGIN = 'https://myspaice.ai';
const ROOM_RE = /^[a-z_]{1,24}$/;
const KINDS = new Set(['house', 'sky']);
const str = (v) => (typeof v === 'string' && v ? v : null);

/** The night's page, or null. `night` = { day, kind } (+ `by` = a resident's
 *  plan: never a page). `template` / `room` = wave-2 fallbacks. */
export function sharePageFor({ day, kind, by, template, room } = {}, { enabled = SHOW_EVENT_SHARE_PAGES, manifest = SHARE_PAGES } = {}) {
  if (!enabled || !manifest) return null;
  let page = null;
  if (str(day) && KINDS.has(kind) && !by) page = (manifest.nights || {})[`${day}|${kind}`] || null;
  if (!page && str(template)) page = (manifest.templates || {})[template] || null;
  if (!page && str(room)) page = (manifest.rooms || {})[room] || null;
  if (!page || typeof page.slug !== 'string' || !/^[a-z0-9-]+$/.test(page.slug)) return null;
  return page;
}

/** The page's public URL. A room (the event's REAL staging room, e.g. a
 *  storm night moved to the library) rides as ?room= only when it differs
 *  from the one the page already knows; the page re-validates it. */
export function pageUrl(page, room = null) {
  if (!page || !page.slug) return null;
  const r = str(room);
  const extra = r && ROOM_RE.test(r) && r !== page.room ? `?room=${r}` : '';
  return `${CANONICAL_ORIGIN}/e/${page.slug}.html${extra}`;
}

/** Today's room link (unchanged since 09-23), or null without a game or a room. */
export function roomLinkFor(gameId, room, origin = (typeof window !== 'undefined' ? window.location.origin : CANONICAL_ORIGIN)) {
  if (!gameId || !room) return null;
  return `${origin}/game-step?gameId=${encodeURIComponent(String(gameId))}&room=${encodeURIComponent(String(room))}&view=peek`;
}

/** The night the house is having right now (or today), as the calendar
 *  payload says: the RUNNING featured house/sky row first, else today's.
 *  `template` (the live event's) must match when the row knows its own, so a
 *  noon bake-off on a meteor day never hands out the meteor page. */
export function nightOfCalendar(cal, template = null) {
  const rows = (cal && Array.isArray(cal.rows)) ? cal.rows : [];
  const ok = (r) => r && r.featured && KINDS.has(r.kind) && !r.by && str(r.day)
    && !(template && r.template && r.template !== template);
  const running = rows.find((r) => ok(r) && r.phase === 'running');
  if (running) return { day: running.day, kind: running.kind };
  if (template && rows.some((r) => r && r.phase === 'running')) return null; // something else is on
  const today = cal && str(cal.today);
  const t = today ? rows.find((r) => ok(r) && r.day === today) : null;
  return t ? { day: t.day, kind: t.kind } : null;
}

/** The house's share for what is on now: { url, title }. The night's page
 *  (title "<night> at the Claude House"), else today's room link with NO title
 *  (the plate's share sheet got `{ url }` only before 10-01: same with the
 *  switch off). Read the page state at CLICK time (GameStepPage's liveEvent is
 *  declared below its share handler: reading it during render is a TDZ).
 *  🔗 10-01 event links (review): one call hands GameStepPage both halves. */
export function houseShareFor({ gameId, room, calendar, template } = {}, opts) {
  const night = nightOfCalendar(calendar, template);
  const page = sharePageFor({ ...(night || {}), template }, opts);
  return page ? { url: pageUrl(page, room), title: shareTitleFor(page) } : { url: roomLinkFor(gameId, room), title: null };
}

/** The URL half of houseShareFor. */
export function houseShareUrl(args = {}, opts) {
  return houseShareFor(args, opts).url;
}

/** 🔗 10-01 event links (review): the house's Share, whole, for GameStepPage's
 *  plate chip + event scene (its shareRoomLink calls ONLY this, at click time).
 *  Switch off / no page = today's room link and a `{ url }`-only share sheet
 *  (the 09-23 plate exactly); a clipboard copy toasts "Link copied" as before.
 *  New with this path: a refused (not cancelled) share sheet falls back to the
 *  clipboard, and a failed copy says "Could not copy the link" (the calendar's
 *  share already did both). Never throws. → shareLink's result. */
export function shareHouse({ gameId, room, calendar, template, onToast = null } = {}, opts) {
  let share;
  try { share = houseShareFor({ gameId, room, calendar, template }, opts); } catch (e) { return Promise.resolve('failed'); }
  return shareLink(share.url, { title: share.title, onToast });
}

/** A share title for the sheet: "Apple picking at the Claude House". */
export const shareTitleFor = (page, fallback = 'The Claude House') => (page && page.label ? `${page.label} at the Claude House` : fallback);

/** navigator.share where it exists (phones), else the clipboard + a toast.
 *  A cancelled share sheet is not an error and says nothing.
 *  `title: null` = the sheet gets `{ url }` only (the plate's room link, as before).
 *  → 'shared' | 'copied' | 'cancelled' | 'failed' (no url = 'failed', silent). */
export async function shareLink(url, { title = 'The Claude House', onToast = null } = {}) {
  if (!url) return 'failed';
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (nav && typeof nav.share === 'function') {
    try { await nav.share(title ? { title, url } : { url }); return 'shared'; } catch (e) { if (e && e.name === 'AbortError') return 'cancelled'; }
  }
  try {
    if (!nav || !nav.clipboard || typeof nav.clipboard.writeText !== 'function') throw new Error('no clipboard');
    await nav.clipboard.writeText(url);
    if (onToast) onToast('Link copied');
    return 'copied';
  } catch (e) {
    if (onToast) onToast('Could not copy the link');
    return 'failed';
  }
}
